import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/ui/AppShell";
import BottomNav from "../components/ui/BottomNav";
import ServiceSearchBar from "../components/search/ServiceSearchBar";
import CategorySection from "../components/discovery/CategorySection";
import { allServicesIndex } from "../data/catalog/allServicesIndex";

const categoryOrder = [
  "Construction & Labour",
  "Repairs & Installation",
  "Appliance Services",
  "Home Help & Lifestyle",
  "Quick Help",
  "Marketplace",
  "Professional & Project Services",
];

function AllServices() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  const filteredServices = useMemo(() => {
    const q = query.trim().toLowerCase();

    return allServicesIndex.filter((service) => {
      const categoryMatch =
        activeCategory === "All" || service.category === activeCategory;

      const searchText = [
        service.title,
        service.category,
        ...(service.tags || []),
      ]
        .join(" ")
        .toLowerCase();

      const queryMatch = !q || searchText.includes(q);

      return categoryMatch && queryMatch;
    }).sort((a, b) => {
      const categoryA = categoryOrder.indexOf(a.category);
      const categoryB = categoryOrder.indexOf(b.category);

      if (categoryA !== categoryB) {
        return categoryA - categoryB;
      }

      return a.title.localeCompare(b.title);
    });
  }, [query, activeCategory]);

  return (
    <AppShell maxWidth="1240px">
      <button
        onClick={() => navigate("/")}
        style={backBtn}
        className="all-services__back-button"
      >
        ← Back to Home
      </button>

      <div style={hero}>
        <h1 style={{ margin: 0 }} className="all-services__title">All Services</h1>
        <p style={heroText} className="all-services__hero-text">
          Search across repairs, appliances, labour, home help, quick help and
          marketplace services, with construction, professional, and labour-led execution prioritized first.
        </p>
      </div>

      <div style={priorityBanner}>
        <div style={priorityTitle}>Prime Product Focus</div>
        <div style={priorityText}>
          Construction and labour services are placed first here because they are
          the lead execution lane for MistriBazaar.
        </div>
      </div>

      <ServiceSearchBar
        value={query}
        onChange={setQuery}
        placeholder="Search plumber, AC, maid, gardening, tutor..."
        autoFocus={false}
      />

      <div style={chipRow} className="all-services__chip-row">
        <button
          onClick={() => setActiveCategory("All")}
          style={{
            ...chip,
            background: activeCategory === "All" ? "#fff" : "#111",
            color: activeCategory === "All" ? "#000" : "#fff",
          }}
        >
          All
        </button>

        {categoryOrder.map((category) => (
          <button
            key={category}
            onClick={() => setActiveCategory(category)}
            style={{
              ...chip,
              background: activeCategory === category ? "#fff" : "#111",
              color: activeCategory === category ? "#000" : "#fff",
            }}
          >
            {category}
          </button>
        ))}
      </div>

      <CategorySection
        title={
          query || activeCategory !== "All"
            ? `Results (${filteredServices.length})`
            : "Browse services"
        }
      >
        {filteredServices.length === 0 ? (
          <div style={emptyBox}>
            No services found for your search.
          </div>
        ) : (
          <div style={grid} className="all-services__grid">
            {filteredServices.map((service) => (
              <button
                key={`${service.category}-${service.title}`}
                onClick={() => navigate(service.path)}
                style={card}
                className="all-services__card"
              >
                <div style={icon}>{service.icon}</div>
                <div style={{ textAlign: "left" }}>
                  <div style={cardTitle}>{service.title}</div>
                  <div style={cardCategory}>{service.category}</div>
                  <div style={tagRow}>
                    {(service.tags || []).slice(0, 4).map((serviceTag) => (
                      <span key={serviceTag} style={tag}>
                        {serviceTag}
                      </span>
                    ))}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </CategorySection>

      <BottomNav />
    </AppShell>
  );
}

const backBtn = {
  marginBottom: "20px",
  padding: "10px 16px",
  border: "1px solid #444",
  background: "#111",
  color: "#fff",
  borderRadius: "10px",
  cursor: "pointer",
};

const hero = {
  marginBottom: "18px",
};

const heroText = {
  color: "#cfcfcf",
  lineHeight: "1.8",
  marginTop: "10px",
};

const priorityBanner = {
  marginBottom: "18px",
  background: "#151008",
  border: "1px solid #3b2d1d",
  borderRadius: "18px",
  padding: "16px 18px",
};

const priorityTitle = {
  color: "#fff1cc",
  fontWeight: "bold",
  marginBottom: "8px",
};

const priorityText = {
  color: "#d4c7ad",
  lineHeight: "1.7",
};

const chipRow = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  marginTop: "16px",
};

const chip = {
  padding: "10px 14px",
  border: "1px solid #2a2a2a",
  borderRadius: "999px",
  cursor: "pointer",
};

const emptyBox = {
  background: "#111",
  border: "1px solid #222",
  borderRadius: "18px",
  padding: "18px",
  color: "#cfcfcf",
};

const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
  gap: "16px",
};

const card = {
  border: "1px solid #262626",
  background: "linear-gradient(180deg, #171717, #111)",
  color: "#fff",
  borderRadius: "18px",
  padding: "18px",
  cursor: "pointer",
  display: "flex",
  gap: "14px",
  alignItems: "flex-start",
  textAlign: "left",
  boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
};

const icon = {
  fontSize: "26px",
  lineHeight: 1,
};

const cardTitle = {
  fontWeight: "bold",
  fontSize: "17px",
};

const cardCategory = {
  color: "#9f9f9f",
  marginTop: "6px",
  fontSize: "13px",
};

const tagRow = {
  display: "flex",
  gap: "8px",
  flexWrap: "wrap",
  marginTop: "12px",
};

const tag = {
  fontSize: "12px",
  padding: "6px 10px",
  borderRadius: "999px",
  border: "1px solid #2c2c2c",
  background: "#0d0d0d",
  color: "#d1d1d1",
};

export default AllServices;
